import classNames from 'classnames';

import type { TagSize, Variant as TagVariant } from './types';
import type { AvatarSizes } from '../Avatar';
import type { IconSizes } from '../Buttons/IconButton';

const tagContainerClassNameBySize: { [key in TagSize]: string } = {
  sm: 'text-xs',
  md: 'text-b3',
  lg: 'text-b2',
};

const makeTagContainerPaddingClassName = ({
  size,
  hasIcon,
  hasClose,
  rightIcon,
  hasLabel,
}: {
  size: TagSize;
  hasIcon: boolean;
  hasClose: boolean;
  rightIcon: boolean;
  hasLabel: boolean;
}): string =>
  classNames(
    {
      'py-3xs': size === 'md',
      'py-xxs': size === 'lg',
    },
    {
      'px-xxs': size === 'sm',
      'pr-xs': size !== 'sm' && hasLabel && ((size === 'md' && !hasClose) || size === 'lg'),
      'pr-xxs': size !== 'sm' && (!hasLabel || (size === 'md' && hasClose) || rightIcon),
    },
    {
      'pl-xs': size !== 'sm' && !hasIcon,
      'pl-xxs': size !== 'sm' && hasIcon,
    },
  );

const tagContainerClassNameByVariant: { [key in TagVariant]: string } = {
  light: 'ctn-neutral-grey-strong text-primary',
  disabled: 'ctn-neutral-grey-weak text-disabled',
  info: 'ctn-info-medium text-inverse',
  warning: 'ctn-warning-medium text-inverse',
  success: 'ctn-success-medium text-inverse',
  error: 'ctn-danger-medium text-inverse',
  primary: 'ctn-brand-medium text-inverse',
  secondary: 'ctn-secondary-medium text-inverse',
  contextual: 'ctn-contextual-medium text-inverse',
};

const borderTagContainerClassNameByVariant: { [key in TagVariant]: string } = {
  light: 'ctn-neutral-grey-weak text-primary outline',
  disabled: 'ctn-neutral-grey-weak text-disabled outline',
  info: 'ctn-info-weak text-info-strong outline outline-strong-info',
  warning: 'ctn-warning-weak text-warning-strong outline outline-strong-warning',
  success: 'ctn-success-weak text-success-strong outline outline-strong-success',
  error: 'ctn-danger-weak text-danger-strong outline outline-strong-danger',
  primary: 'ctn-brand-weak text-brand outline outline-brand',
  secondary: 'ctn-secondary-weak text-blue outline outline-secondary',
  contextual: 'ctn-contextual-weak text-contextual-strong outline outline-strong-contextual',
};

export const sizeForIconButton: { [key in TagSize]: IconSizes } = {
  sm: 'xxs',
  md: 'xxs',
  lg: 'xs',
};

export const iconContainerSize: { [key in TagSize]: string } = {
  sm: 'w-sm h-sm',
  md: 'w-md h-md',
  lg: 'w-lg h-lg',
};

export const avatarSizeMapper: { [K in TagSize]?: AvatarSizes } = {
  sm: '3xs',
  md: 'xxs',
  lg: 'sm',
};

export const makeTagContainerClassName = ({
  variant,
  size,
  hasIcon,
  hasClose,
  className,
  rightIcon,
  isBorder,
  hasLabel,
}: {
  variant: TagVariant;
  size: TagSize;
  hasIcon: boolean;
  hasClose: boolean;
  className?: string;
  rightIcon: boolean;
  isBorder?: boolean;
  hasLabel: boolean;
}): string =>
  classNames(
    isBorder ? 'rounded-md' : 'rounded-xxs',
    'inline-flex items-center',
    tagContainerClassNameBySize[size],
    makeTagContainerPaddingClassName({ size, hasIcon, hasClose, rightIcon, hasLabel }),
    isBorder
      ? borderTagContainerClassNameByVariant[variant]
      : tagContainerClassNameByVariant[variant],
    className,
  );

export const makeIconContainerClassName = ({ size }: { size: TagSize }): string =>
  classNames('shrink-0 flex justify-center items-center', iconContainerSize[size]);

export const makeRightMarginClassName = ({
  size,
  hasLabel,
}: {
  size: TagSize;
  hasLabel: boolean;
}): string => classNames({ [size === 'sm' ? 'mr-3xs' : 'mr-xxs']: hasLabel });

export const makeLeftMarginClassName = ({
  size,
  hasLabel,
}: {
  size: TagSize;
  hasLabel: boolean;
}): string => classNames({ [size === 'sm' ? 'ml-3xs' : 'ml-xxs']: hasLabel });
